import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useModalTranslation } from '../hooks/useTranslation';
import styles from './DeviceScreenPreviews.module.css';

/**
 * Mock renders of the idle screens the device's 296×128 e-paper panel shows,
 * used as row previews in the Display Layout picker. Content is illustrative
 * sample data; the panel itself is drawn at its native pixel size and scaled
 * to the row width.
 */

const PANEL_WIDTH = 296;
const PANEL_HEIGHT = 128;

// 1-bit glyphs matching the panel render service, drawn with currentColor so
// the inverted tile header can flip them to paper.
const HomeGlyph = ({ size }: { size: number }) => (
    <svg width={size} height={size} viewBox="0 0 512 512" className={styles.glyph} aria-hidden="true">
        <path d="M261.56 101.28a8 8 0 0 0-11.06 0L66.4 277.15a8 8 0 0 0-2.47 5.79L63.9 448a32 32 0 0 0 32 32H192a16 16 0 0 0 16-16V328a8 8 0 0 1 8-8h80a8 8 0 0 1 8 8v136a16 16 0 0 0 16 16h96.06a32 32 0 0 0 32-32V282.94a8 8 0 0 0-2.47-5.79Z" fill="currentColor" />
        <path d="m490.91 244.15-74.8-71.56V64a16 16 0 0 0-16-16h-48a16 16 0 0 0-16 16v32l-57.92-55.38C272.77 35.14 264.71 32 256 32c-8.68 0-16.72 3.14-22.14 8.63l-212.7 203.5c-6.22 6-7 15.87-1.34 22.37A16 16 0 0 0 43 267.56L250.5 69.28a8 8 0 0 1 11.06 0l207.52 198.28a16 16 0 0 0 22.59-.44c6.14-6.36 5.63-16.86-.76-22.97" fill="currentColor" />
    </svg>
);

const OutGlyph = ({ size }: { size: number }) => (
    <svg width={size} height={size} viewBox="0 0 512 512" className={styles.glyph} aria-hidden="true">
        <path d="m268 112 144 144-144 144M392 256H100" fill="none" stroke="currentColor" strokeWidth="56" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const EyeGlyph = ({ size }: { size: number }) => (
    <svg width={size} height={size} viewBox="0 0 512 512" className={styles.glyph} aria-hidden="true">
        <circle cx="256" cy="256" r="64" fill="currentColor" />
        <path d="M490.84 238.6c-26.46-40.92-60.79-75.68-99.27-100.53C349 110.55 302 96 255.66 96c-42.52 0-84.33 12.15-124.27 36.11-40.73 24.43-77.63 60.12-109.68 106.07a31.92 31.92 0 0 0-.64 35.54c26.41 41.33 60.4 76.14 98.28 100.65C162 402 207.9 416 255.66 416c46.71 0 93.81-14.43 136.2-41.72 38.46-24.77 72.72-59.66 99.08-100.92a32.2 32.2 0 0 0-.1-34.76M256 352a96 96 0 1 1 96-96 96.11 96.11 0 0 1-96 96" fill="currentColor" />
    </svg>
);

const LockGlyph = ({ size }: { size: number }) => (
    <svg width={size} height={size} viewBox="0 0 512 512" fill="none" stroke="currentColor" strokeWidth="44" strokeLinecap="round" strokeLinejoin="round" className={styles.glyph} aria-hidden="true">
        <path d="M336 208v-95a80 80 0 0 0-160 0v95" />
        <rect width="320" height="272" x="96" y="208" rx="48" ry="48" />
    </svg>
);

const WifiGlyph = () => (
    <svg width="13" height="10" viewBox="0 0 16 13" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M1 4 A10 10 0 0 1 15 4" />
        <path d="M4 7.5 A6 6 0 0 1 12 7.5" />
        <rect x="7" y="10" width="2" height="2" fill="currentColor" stroke="none" />
    </svg>
);

const BatteryGlyph = () => (
    <svg width="17" height="10" viewBox="0 0 20 12" fill="none" stroke="currentColor" strokeWidth="1" aria-hidden="true">
        <rect x="0.5" y="1.5" width="16" height="9" />
        <rect x="17.5" y="4.5" width="2" height="3" fill="currentColor" />
        <path d="M9.5 2.5 L5.5 6.5 H8.5 L7.5 9.5 L11.5 5.5 H8.5 Z" fill="currentColor" stroke="none" />
    </svg>
);

// The status strip every layout keeps along the top.
const StatusStrip = ({ policy }: { policy: string }) => (
    <div className={styles.strip}>
        <LockGlyph size={12} />
        <span className={styles.stripPolicy}>{policy}</span>
        <span className={styles.stripStatus}>
            <WifiGlyph />
            <BatteryGlyph />
        </span>
    </div>
);

/** Draws the panel at its native 296×128 size and scales it to fill the container width. */
const PanelFrame = ({ policy, children }: { policy: string, children: ReactNode }) => {
    const frameRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);

    useLayoutEffect(() => {
        const frame = frameRef.current;
        if (!frame) {
            return;
        }
        const update = () => setScale(frame.clientWidth / PANEL_WIDTH);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(frame);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={frameRef} className={styles.frame} style={{ height: PANEL_HEIGHT * scale }}>
            <div className={styles.panel} style={{ transform: `scale(${scale})` }}>
                <StatusStrip policy={policy} />
                {children}
            </div>
        </div>
    );
};

/** "Last Event": the most recent event alone — icon box, name headline, verb + time. */
export const LastEventPreview = () => {
    const { t } = useModalTranslation();
    return (
        <PanelFrame policy={t('device_screen.preview.policy')}>
            <div className={styles.lastEvent}>
                <div className={styles.lastEventIcon}><HomeGlyph size={38} /></div>
                <div className={styles.lastEventBody}>
                    <div className={styles.lastEventText}>
                        <span className={styles.lastEventTitle}>Jinx</span>
                        <span className={styles.lastEventRule} />
                        <span className={styles.lastEventTime}>
                            {t('push_notification_settings.transit_inward')}&ensp;19:40
                        </span>
                    </div>
                </div>
            </div>
        </PanelFrame>
    );
};

/** "Latest & Recent": a headline for the latest event over rows for the three before it. */
export const LatestRecentPreview = () => {
    const { t } = useModalTranslation();
    const row = (time: string, icon: ReactNode, name: string, verb: string) => (
        <div className={styles.feedRow}>
            <span className={styles.feedRowTime}>{time}</span>
            {icon}
            <span className={styles.feedRowName}>{name}</span>
            <span className={styles.feedRowVerb}>{verb}</span>
        </div>
    );
    return (
        <PanelFrame policy={t('device_screen.preview.policy')}>
            <div className={styles.feed}>
                <div className={styles.feedHeadline}>
                    <HomeGlyph size={22} />
                    <span className={styles.feedHeadlineName}>Jinx</span>
                    <span className={styles.feedHeadlineTime}>19:40</span>
                </div>
                <div className={styles.feedRows}>
                    {row('19:12', <OutGlyph size={13} />, 'Jinx', t('push_notification_settings.transit_outward'))}
                    {row('17:36', <EyeGlyph size={13} />, 'Luna', t('push_notification_settings.peek_inward'))}
                    {row(t('device_screen.preview.time_went_out'), <HomeGlyph size={13} />, 'Fred', t('push_notification_settings.transit_inward'))}
                </div>
            </div>
        </PanelFrame>
    );
};

/** "Inside & Outside": two columns of cats by presence, with a count for each. */
export const InsideOutsidePreview = () => {
    const { t } = useModalTranslation();
    const row = (name: string, icon: ReactNode, time: string) => (
        <div className={styles.splitRow}>
            <span className={styles.splitRowName}>{name}</span>
            {icon}
            <span className={styles.splitRowTime}>{time}</span>
        </div>
    );
    return (
        <PanelFrame policy={t('device_screen.preview.policy')}>
            <div className={styles.split}>
                <div className={styles.splitColumn}>
                    <div className={`${styles.splitHeader} ${styles.splitHeaderInside}`}>
                        <span className={styles.splitHeaderLabel}>{t('rfid_profile.inside').toLocaleUpperCase()}</span>
                        <span className={styles.splitHeaderCount}>2</span>
                    </div>
                    <div className={`${styles.splitBody} ${styles.splitBodyInside}`}>
                        {row('Jinx', <HomeGlyph size={11} />, '19:40')}
                        {row('Fred', <HomeGlyph size={11} />, '15:08')}
                    </div>
                </div>
                <div className={styles.splitColumn}>
                    <div className={`${styles.splitHeader} ${styles.splitHeaderOutside}`}>
                        <span className={styles.splitHeaderLabel}>{t('rfid_profile.outside').toLocaleUpperCase()}</span>
                        <span className={styles.splitHeaderCount}>2</span>
                    </div>
                    <div className={`${styles.splitBody} ${styles.splitBodyOutside}`}>
                        {row('Luna', <OutGlyph size={11} />, '19:12')}
                        {row('Pip', <OutGlyph size={11} />, '06:48')}
                    </div>
                </div>
            </div>
        </PanelFrame>
    );
};

const Tile = ({ inside, icon, name, event, time }: {
    inside?: boolean,
    icon: ReactNode,
    name: string,
    event: string,
    time: string,
}) => (
    <div className={styles.tile}>
        <div className={inside ? `${styles.tileHeader} ${styles.tileHeaderInside}` : styles.tileHeader}>{icon}</div>
        <div className={styles.tileBody}>
            <span className={styles.tileName}>{name}</span>
            <span className={styles.tileEvent}>{event}</span>
            <span className={styles.tileTime}>{time}</span>
        </div>
    </div>
);

/** The Activity board ('5a'): a tile per cat with its last event. */
export const ActivityBoardPreview = () => {
    const { t } = useModalTranslation();
    return (
        <PanelFrame policy={t('device_screen.preview.policy')}>
            <div className={styles.tiles}>
                <Tile inside icon={<HomeGlyph size={18} />} name="Jinx" event={t('push_notification_settings.transit_inward')} time="08:04" />
                <Tile icon={<OutGlyph size={18} />} name="Fred" event={t('push_notification_settings.transit_outward')} time={t('device_screen.preview.time_went_out')} />
                <Tile icon={<EyeGlyph size={18} />} name="Luna" event={t('push_notification_settings.peek_inward')} time="09/07" />
            </div>
        </PanelFrame>
    );
};
